import React, { useState, useEffect } from 'react';
import { Sparkles } from 'lucide-react';

interface LoadingScreenProps {
  onComplete: () => void;
}

export const LoadingScreen: React.FC<LoadingScreenProps> = ({ onComplete }) => {
  const [progress, setProgress] = useState(0);
  const [isExiting, setIsExiting] = useState(false);

  useEffect(() => {
    // Smooth progress counter up to 100% over ~2.0 seconds
    const interval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) {
          clearInterval(interval);
          return 100;
        }
        const next = prev + Math.floor(Math.random() * 9) + 5;
        return next > 100 ? 100 : next;
      });
    }, 50);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (progress === 100) {
      const exitTimer = setTimeout(() => {
        setIsExiting(true);
        setTimeout(() => {
          onComplete();
        }, 550);
      }, 350);

      return () => clearTimeout(exitTimer);
    }
  }, [progress, onComplete]);

  return (
    <div className={`loading-screen ${isExiting ? 'exit' : ''}`}>
      {/* Soft Ambient Aura Mesh */}
      <div className="minimal-loader-aura aura-cyan"></div>
      <div className="minimal-loader-aura aura-purple"></div>

      <div className="minimal-loader-content">
        {/* Glowing Monogram Circle */}
        <div className="minimal-logo-frame">
          <svg className="minimal-progress-ring" viewBox="0 0 100 100">
            <circle
              className="ring-bg"
              cx="50"
              cy="50"
              r="44"
            />
            <circle
              className="ring-fill"
              cx="50"
              cy="50"
              r="44"
              style={{
                strokeDasharray: 276,
                strokeDashoffset: 276 - (276 * progress) / 100
              }}
            />
          </svg>
          <div className="minimal-badge-inner">
            <span className="minimal-badge-text">LS</span>
            <Sparkles size={14} className="sparkle-badge-icon" />
          </div>
        </div>

        {/* Minimalist Name & Tagline */}
        <h1 className="minimal-loader-name">
          LORENZ<span className="dot">.</span>DEV
        </h1>
        <p className="minimal-loader-tag">
          Web Developer & IT Support Specialist
        </p>

        {/* Bouncing Programming Language & AI Icons Row */}
        <div className="bouncing-icons-row">
          {/* Claude Code */}
          <div className="bouncing-icon bounce-1" title="Claude Code">
            <img src="/assets/claude Icon.png" alt="Claude Code" className="loader-img-icon" />
          </div>

          {/* Google AntiGravity */}
          <div className="bouncing-icon bounce-2" title="Google AntiGravity">
            <img src="/assets/ANTI GRAVITY ICON.png" alt="Google AntiGravity" className="loader-img-icon" />
          </div>

          {/* OpenAI Codex */}
          <div className="bouncing-icon bounce-3" title="OpenAI Codex">
            <img src="/assets/codex-icon.webp" alt="OpenAI Codex" className="loader-img-icon" />
          </div>

          {/* Laravel */}
          <div className="bouncing-icon bounce-4" title="Laravel">
            <img src="/assets/Laravel Icon.png" alt="Laravel" className="loader-img-icon" />
          </div>

          {/* Canva */}
          <div className="bouncing-icon bounce-5" title="Canva">
            <img src="/assets/Canva Icon.jpg" alt="Canva" className="loader-img-icon" style={{ borderRadius: '4px' }} />
          </div>

          {/* CapCut */}
          <div className="bouncing-icon bounce-6" title="CapCut">
            <img src="/assets/CAPCUT ICON.png" alt="CapCut" className="loader-img-icon" />
          </div>

          {/* SQL / MySQL */}
          <div className="bouncing-icon bounce-1" title="SQL Database">
            <img src="/assets/SQL Icon.png" alt="SQL Database" className="loader-img-icon" />
          </div>
        </div>

        {/* Minimalist Progress Counter */}
        <div className="minimal-progress-box">
          <div className="minimal-progress-bar-track">
            <div
              className="minimal-progress-bar-fill"
              style={{ width: `${progress}%` }}
            ></div>
          </div>
          <span className="minimal-counter-num">{progress}%</span>
        </div>
      </div>
    </div>
  );
};
